<template>
  <div class="analytics-dashboard" :class="{ 'nav-collapsed': isNavCollapsed }">
    <Navigation @nav-toggle="handleNavToggle" />

    <div class="dashboard-content">
      <div class="page-container">
        <header class="page-header page-header-hero" role="banner">
          <div class="page-header__top">
            <span class="page-eyebrow">County human resource intelligence</span>
            <div class="header-row">
              <div class="title-block">
                <h1 class="page-title">{{ dashboardPageTitle }}</h1>
                <p class="page-subtitle">{{ dashboardSubtitle }}</p>
              </div>
              <div class="header-actions">
                <div class="period-selector" aria-label="Reporting month">
                  <button type="button" class="period-btn" @click="previousPeriod" aria-label="Previous month">
                    <i class="bx bx-chevron-left"></i>
                  </button>
                  <span class="current-period">{{ currentPeriodLabel }}</span>
                  <button type="button" class="period-btn" @click="nextPeriod" :disabled="!canGoNextPeriod" aria-label="Next month">
                    <i class="bx bx-chevron-right"></i>
                  </button>
                </div>
                <button type="button" class="btn-gov btn-gov--primary" @click="refreshDashboard" :disabled="refreshing">
                  <i class="bx bx-refresh" :class="{ 'spin': refreshing }"></i>
                  {{ refreshing ? 'Refreshing…' : 'Refresh data' }}
                </button>
                <button type="button" class="btn-gov btn-gov--ghost" @click="logoutDashboard">
                  <i class="bx bx-log-out"></i> Sign out
                </button>
              </div>
            </div>
          </div>

          <div class="welcome-banner">
            <div class="welcome-banner__accent" aria-hidden="true"></div>
            <div class="welcome-text">
              <p class="welcome-kicker">Signed in as</p>
              <h2 class="welcome-title">{{ displayName }}</h2>
              <p class="welcome-date">{{ formattedCurrentDate }}</p>
            </div>
          </div>
        </header>

        <section class="dash-section" aria-labelledby="metrics-heading">
          <div class="section-heading">
            <h2 id="metrics-heading" class="section-heading__title">Key establishment indicators</h2>
            <p class="section-heading__desc">Live counts scoped to your access level. Select a card to open the related register or workflow.</p>
          </div>
        <div class="metrics-grid">
          <div class="metric-card metric-card--navy" @click="goToEmployeeRegister">
            <div class="metric-icon">
              <i class="bx bx-group"></i>
            </div>
            <div class="metric-content">
              <span class="metric-label">Total Establishment</span>
              <span class="metric-value">{{ formatNumber(metrics.totalEmployees) }}</span>
              <span class="metric-trend" v-if="metrics.newEmployeeRate > 0">
                <i class="bx bx-up-arrow-alt"></i> {{ metrics.newEmployeeRate }}%
              </span>
            </div>
          </div>

          <div class="metric-card metric-card--teal" @click="goToHRActions">
            <div class="metric-icon">
              <i class="bx bx-user-plus"></i>
            </div>
            <div class="metric-content">
              <span class="metric-label">Added This Month</span>
              <span class="metric-value">{{ formatNumber(metrics.employeesAddedThisMonth) }}</span>
              <span class="metric-badge">New Hires</span>
            </div>
          </div>

          <div class="metric-card metric-card--warn" @click="goToAttendance">
            <div class="metric-icon">
              <i class="bx bx-user-x"></i>
            </div>
            <div class="metric-content">
              <span class="metric-label">Absent (Unauthorized)</span>
              <span class="metric-value">{{ formatNumber(metrics.absentWithoutPermission) }}</span>
              <span class="metric-trend negative">
                <i class="bx bx-error"></i> {{ metrics.absentWithoutPermissionRate }}%
              </span>
            </div>
          </div>

          <div class="metric-card metric-card--slate" @click="goToLeaveManagement">
            <div class="metric-icon">
              <i class="bx bx-user-check"></i>
            </div>
            <div class="metric-content">
              <span class="metric-label">Absent (Authorized)</span>
              <span class="metric-value">{{ formatNumber(metrics.absentWithPermission) }}</span>
              <span class="metric-badge">Approved Leave</span>
            </div>
          </div>
        </div>
        </section>

        <section class="dash-section dash-section--charts" aria-labelledby="analytics-heading">
          <div class="section-heading section-heading--compact">
            <h2 id="analytics-heading" class="section-heading__title">Workforce analytics</h2>
            <p class="section-heading__desc">Attendance, composition, and distribution. Export charts where available for reporting packs.</p>
          </div>
        <div class="charts-grid">
          <!-- Attendance Summary -->
          <div class="chart-card chart-card-wide">
            <div class="card-header">
              <div>
                <h3 class="card-title">
                  <i class="bx bx-calendar-check"></i> Attendance Summary
                </h3>
                <p class="card-desc">
                  Summary for <strong>{{ attendanceChartRangeLabel || attendancePeriodDisplay }}</strong>: employees who <strong>clocked in</strong> at least once vs those who <strong>did not</strong>, and daily counts on the chart (timesheets and attendance records).
                </p>
              </div>
              <div class="card-controls attendance-chart-controls">
                <label class="chart-date-label">
                  <span>From</span>
                  <input
                    v-model="attendanceChartStartDate"
                    type="date"
                    class="chart-date-input"
                    :max="attendanceChartEndDate || todayIsoDate"
                    @change="onAttendanceChartRangeChange"
                  >
                </label>
                <label class="chart-date-label">
                  <span>To</span>
                  <input
                    v-model="attendanceChartEndDate"
                    type="date"
                    class="chart-date-input"
                    :min="attendanceChartStartDate"
                    :max="todayIsoDate"
                    @change="onAttendanceChartRangeChange"
                  >
                </label>
                <button type="button" class="control-btn" @click="exportChart('attendance')" title="Export chart">
                  <i class="bx bx-download"></i>
                </button>
              </div>
            </div>
            <div class="card-body">
              <div v-if="attendanceSummarySummary && attendanceRateDisplay != null" class="attendance-kpis">
                <div class="kpi-pill kpi-primary">
                  <span class="kpi-label">Attendance rate</span>
                  <span class="kpi-value">{{ attendanceRateDisplay }}%</span>
                  <span class="kpi-hint">{{ attendanceRateHint }}</span>
                </div>
                <div class="kpi-pill">
                  <span class="kpi-label">Clocked in</span>
                  <span class="kpi-value">{{ formatNumber(attendanceSummarySummary.clockedInEmployees) }}</span>
                  <span class="kpi-hint">Employees with at least one clock-in in period</span>
                </div>
                <div class="kpi-pill">
                  <span class="kpi-label">Not clocked in</span>
                  <span class="kpi-value">{{ formatNumber(attendanceSummarySummary.notClockedInEmployees) }}</span>
                  <span class="kpi-hint">Active employees with no clock-in in period</span>
                </div>
                <div class="kpi-pill">
                  <span class="kpi-label">Total employees</span>
                  <span class="kpi-value">{{ formatNumber(attendanceSummarySummary.totalEmployees) }}</span>
                  <span class="kpi-hint">Active staff as of {{ attendanceSummarySummary.endDate || 'period end' }}</span>
                </div>
              </div>
              <div id="AttendanceContainer" ref="attendanceChart"></div>
              <div v-if="!attendanceData" class="chart-placeholder">
                <div class="spinner sm"></div>
                <p>Loading data...</p>
              </div>
            </div>
          </div>

          <!-- Today's Attendance -->
          <div class="chart-card">
            <div class="card-header">
              <h3 class="card-title">
                <i class="bx bx-time"></i> Today's Attendance
              </h3>
              <div class="card-controls">
                <span class="date-badge">{{ formattedCurrentDate }}</span>
                <button class="control-btn" @click="refreshTodayAttendance" title="Refresh">
                  <i class="bx bx-refresh"></i>
                </button>
              </div>
            </div>
            <div class="card-body">
              <div id="TodayAttendanceContainer" ref="todayAttendanceChart"></div>
              <div v-if="!todayAttendanceData" class="chart-placeholder">
                <div class="spinner sm"></div>
                <p>Loading data...</p>
              </div>
              <div class="stats-row" v-if="todayAttendanceData">
                <div class="stat-item">
                  <span class="stat-label">Present</span>
                  <span class="stat-value stat-value--present">{{ todayAttendanceStats.present || 0 }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">Absent</span>
                  <span class="stat-value stat-value--absent">{{ todayAttendanceStats.absent || 0 }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">Late</span>
                  <span class="stat-value stat-value--late">{{ todayAttendanceStats.late || 0 }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Staff headcount by department (county-wide roles only) -->
          <div v-if="showStaffDistribution" class="chart-card chart-card-wide">
            <div class="card-header">
              <div>
                <h3 class="card-title">
                  <i class="bx bx-buildings"></i> Staff by Department
                </h3>
                <p class="card-desc">Headcount by department for <strong>{{ attendancePeriodDisplay }}</strong> (employed through month-end, excluding separations/terminations on or before that date).</p>
              </div>
              <div class="card-controls">
                <span v-if="staffByDepartmentData" class="period-badge">{{ formatNumber(staffByDepartmentData.total || 0) }} staff</span>
                <button type="button" class="control-btn" @click="exportChart('staffDept')" title="Export chart">
                  <i class="bx bx-download"></i>
                </button>
              </div>
            </div>
            <div class="card-body card-body-split">
              <div class="staff-chart-wrap">
                <div id="StaffDeptContainer" ref="staffDeptChart"></div>
                <div v-if="!staffByDepartmentData" class="chart-placeholder">
                  <div class="spinner sm"></div>
                  <p>Loading data...</p>
                </div>
              </div>
              <div class="staff-table-wrap" v-if="staffByDepartmentRows.length">
                <div class="staff-table-head">Departments ({{ staffByDepartmentRows.length }})</div>
                <div class="staff-table-scroll">
                  <table class="staff-dept-table">
                    <thead>
                      <tr><th>Department</th><th class="num">Staff</th><th class="num">Share</th></tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, idx) in staffByDepartmentRows" :key="'sd-' + idx">
                        <td>{{ row.departmentName }}</td>
                        <td class="num">{{ formatNumber(row.count) }}</td>
                        <td class="num text-muted">{{ staffDeptShare(row.count) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <!-- Nature of Exits -->
          <div class="chart-card">
            <div class="card-header">
              <h3 class="card-title">
                <i class="bx bx-exit"></i> Nature of Exits
              </h3>
            </div>
            <div class="card-body">
              <div id="ExitChart" ref="exitChart"></div>
              <div v-if="!natureOfExitData" class="chart-placeholder">
                <div class="spinner sm"></div>
                <p>Loading data...</p>
              </div>
              <div class="stats-grid" v-if="natureOfExitData">
                <div class="stat-row-item" v-for="item in exitSummary" :key="item.name">
                  <span class="stat-label">{{ item.name }}</span>
                  <span class="stat-value">{{ item.value }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Employee Composition (Male, Female, PWDs) -->
          <div class="chart-card">
            <div class="card-header">
              <h3 class="card-title">
                <i class="bx bx-pie-chart-alt"></i> Employee Composition
              </h3>
            </div>
            <div class="card-body">
              <div id="compositionChart" ref="compositionChart"></div>
              <div v-if="!employeeCompositionData" class="chart-placeholder">
                <div class="spinner sm"></div>
                <p>Loading data...</p>
              </div>
            </div>
          </div>

          <!-- Job Groups -->
          <div class="chart-card">
            <div class="card-header">
              <h3 class="card-title">
                <i class="bx bx-group"></i> Job Groups
              </h3>
            </div>
            <div class="card-body">
              <div id="jobGroupsChart" ref="jobGroupsChart"></div>
              <div v-if="!jobGroupsData" class="chart-placeholder">
                <div class="spinner sm"></div>
                <p>Loading data...</p>
              </div>
            </div>
          </div>

          <!-- Active Employees Trend -->
          <div class="chart-card chart-card-wide">
            <div class="card-header">
              <h3 class="card-title">
                <i class="bx bx-line-chart"></i> Active Employees by Month ({{ selectedPeriodYear }})
              </h3>
            </div>
            <div class="card-body">
              <div id="ActiveEmployeesTrendChart" ref="activeEmployeesTrendChart"></div>
              <div v-if="!activeEmployeesTrendData" class="chart-placeholder">
                <div class="spinner sm"></div>
                <p>Loading data...</p>
              </div>
            </div>
          </div>


          <!-- Wage Bill per Department (hidden for Departmental HR) -->
          <div class="chart-card" v-if="showWageBillPerDepartment">
            <div class="card-header">
              <div class="card-header__text">
                <h3 class="card-title">
                  <i class="bx bx-money"></i> Wage Bill per Department
                </h3>
                <p v-if="wageBillByDepartmentData && wageBillTotal != null" class="card-meta-line">
                  Consolidated total: <strong>{{ formatWageBillKes(wageBillTotal) }}</strong> KES
                </p>
              </div>
            </div>
            <div class="card-body">
              <div id="wageBillChart" ref="wageBillChart"></div>
              <div v-if="!wageBillByDepartmentData" class="chart-placeholder">
                <div class="spinner sm"></div>
                <p>Loading data...</p>
              </div>
            </div>
          </div>

          <!-- Gender Distribution -->
          <div class="chart-card">
            <div class="card-header">
              <h3 class="card-title">
                <i class="bx bx-male-female"></i> Gender Distribution
              </h3>
            </div>
            <div class="card-body">
              <div id="GenderContainer" ref="genderChart"></div>
              <div v-if="!genderComparisonData" class="chart-placeholder">
                <div class="spinner sm"></div>
                <p>Loading data...</p>
              </div>
              <div class="stats-row" v-if="genderComparisonData">
                <div class="stat-item">
                  <span class="stat-label">Male</span>
                  <span class="stat-value">{{ genderStats.male || 0 }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">Female</span>
                  <span class="stat-value">{{ genderStats.female || 0 }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Disability Distribution -->
          <div class="chart-card">
            <div class="card-header">
              <h3 class="card-title">
                <i class="bx bx-accessibility"></i> Employees with Disabilities
              </h3>
            </div>
            <div class="card-body">
              <div id="disabilityChart" ref="disabilityChart"></div>
              <div v-if="!disabilityDistribution" class="chart-placeholder">
                <div class="spinner sm"></div>
                <p>Loading data...</p>
              </div>
              <div class="stats-grid" v-if="disabilityDistribution">
                <div class="stat-row-item">
                  <span class="stat-label">With Disabilities</span>
                  <span class="stat-value">{{ disabilityStats.withDisabilities || 0 }}</span>
                </div>
                <div class="stat-row-item">
                  <span class="stat-label">Percentage</span>
                  <span class="stat-value">{{ disabilityStats.percentage || 0 }}%</span>
                </div>
              </div>
            </div>
          </div>

          <div class="chart-card">
            <div class="card-header">
              <h3 class="card-title">
                <i class="bx bx-network-chart"></i> {{ ethnicityTitle }}
              </h3>
            </div>
            <div class="card-body">
              <div class="data-table-scroll">
                <table class="data-table">
                  <thead><tr><th>Ethnicity</th><th class="num">Count</th></tr></thead>
                  <tbody>
                    <tr v-for="(e, i) in ethnicityDistribution" :key="'eth-' + i">
                      <td>{{ e.name }}</td>
                      <td class="num">{{ e.count }}</td>
                    </tr>
                    <tr v-if="!ethnicityDistribution.length"><td colspan="2" class="data-table__empty">No ethnicity data in scope.</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/Navigation.vue";
import Highcharts from 'highcharts';
import 'highcharts/modules/drilldown';
import 'highcharts/modules/exporting';
import 'highcharts/modules/export-data';
import 'highcharts/modules/accessibility';
import { authUrl, execute } from "@/api";

Highcharts.setOptions({
  credits: { enabled: false },
  lang: { thousandsSep: ',', decimalPoint: '.' },
  chart: { backgroundColor: 'transparent', style: { fontFamily: 'inherit' } },
  colors: ['#1e4976', '#0f766e', '#b45309', '#9f1239', '#475569', '#64748b', '#ca8a04']
});

export default {
  name: "Dashboard",
  components: { Navigation },
  data() {
    return {
      isNavCollapsed: false,
      refreshing: false,
      showHelpModal: false,
      
      currentYear: new Date().getFullYear(),
      currentMonth: new Date().toLocaleString('default', { month: 'long' }),
      formattedCurrentDate: new Date().toLocaleDateString('en-US', { 
        year: 'numeric', month: 'long', day: 'numeric' 
      }),
      selectedPeriodYear: new Date().getFullYear(),
      selectedPeriodMonth: new Date().getMonth() + 1,
      attendanceChartStartDate: '',
      attendanceChartEndDate: '',
      attendanceChartLoading: false,
      
      metrics: {
        totalEmployees: 0,
        employeesAddedThisMonth: 0,
        absentWithoutPermission: 0,
        absentWithPermission: 0,
        absentWithoutPermissionRate: 0,
        absentWithPermissionRate: 0,
        newEmployeeRate: 0,
        attendanceRate: 0,
        turnoverRate: 0
      },
      
      attendanceData: null,
      attendanceSummarySummary: null,
      todayAttendanceData: null,
      staffByDepartmentData: null,
      staffTurnoverData: null,
      natureOfExitData: null,
      genderComparisonData: null,
      disabilityDistribution: null,
      employeeCompositionData: null,
      jobGroupsData: null,
      wageBillByDepartmentData: null,
      activeEmployeesTrendData: null,
      
      todayAttendanceStats: { present: 0, absent: 0, late: 0, total: 0 },
      genderStats: { male: 0, female: 0, other: 0 },
      disabilityStats: { withDisabilities: 0, total: 0, percentage: 0 },
      ethnicityDistribution: [],
      exitSummary: [],
      systemUptime: '99.8%',
      chartInstances: {
        attendance: null, todayAttendance: null, staffDept: null,
        exit: null, composition: null, jobGroups: null, wageBill: null,
        gender: null, disability: null, activeEmployeesTrend: null
      },
      dashboardScope: { type: '', departmentId: '', departmentName: '', departmentScoped: false }
    }
  },
  computed: {
    displayName() {
      try {
        const userInfoRaw = sessionStorage.getItem("userInfo");
        if (userInfoRaw) {
          const user = JSON.parse(userInfoRaw);
          return user?.names || sessionStorage.getItem("sessionNames") || "Admin";
        }
        return sessionStorage.getItem("sessionNames") || "Admin";
      } catch {
        return "Admin";
      }
    },
    wageBillTotal() {
      const d = this.wageBillByDepartmentData;
      if (!d) return null;
      if (d.total != null && d.total !== "") {
        const n = Number(d.total);
        return Number.isFinite(n) ? n : null;
      }
      const series = d.chartData?.series;
      const data = Array.isArray(series) && series[0] && Array.isArray(series[0].data) ? series[0].data : [];
      if (!data.length) return 0;
      const sum = data.reduce((acc, v) => acc + (Number(v) || 0), 0);
      return sum;
    },
    /** County-wide UI: API scope first; session role check via methods in loadDashboardData (not computed — sessionStorage is not reactive). */
    isCountyWideDashboard() {
      const t = String(this.dashboardScope?.type || "").toLowerCase();
      return t === "governor" || t === "hr_admin";
    },
    dashboardPageTitle() {
      if (String(this.dashboardScope?.type || "").toLowerCase() === "governor") {
        return "Governor Dashboard";
      }
      return "Dashboard";
    },
    dashboardSubtitle() {
      const dept = this.dashboardScope?.departmentName || '';
      if (this.isDepartmentalScope && dept) {
        return `${dept} human resource operations overview`;
      }
      return 'Homa Bay County human resource operations overview';
    },
    rbacScope() {
      try {
        const raw = sessionStorage.getItem("rbac_context");
        if (!raw) return "";
        const ctx = JSON.parse(raw);
        return String(ctx?.data_scope || "").toUpperCase();
      } catch (_) {
        return "";
      }
    },
    rbacDepartmentId() {
      try {
        const raw = sessionStorage.getItem("rbac_context");
        if (!raw) return "";
        const ctx = JSON.parse(raw);
        return String(ctx?.department_id || "").trim();
      } catch (_) {
        return "";
      }
    },
    selectedRoleNames() {
      try {
        const rolesRaw = sessionStorage.getItem("selectedRoles");
        const roles = rolesRaw ? JSON.parse(rolesRaw) : [];
        if (!Array.isArray(roles)) return [];
        return roles
          .map((r) => String(r?.role_name || r?.roleName || "").trim().toUpperCase())
          .filter(Boolean);
      } catch (_) {
        return [];
      }
    },
    isDepartmentalHrRole() {
      return this.selectedRoleNames.some((name) =>
        name.includes("DEPARTMENTAL HR") || name === "DEPARTMENTAL_HR"
      );
    },
    isDepartmentalScope() {
      if (this.isCountyWideDashboard) return false;
      if (this.dashboardScope?.departmentScoped === true) return true;
      if (this.rbacScope === 'DEPARTMENT' || this.rbacScope === 'SELF') return true;
      const type = String(this.dashboardScope?.type || '').toLowerCase();
      return type === 'department' || this.isDepartmentalHrRole;
    },
    effectiveDepartmentId() {
      if (this.isCountyWideDashboard) return "";
      if (this.rbacDepartmentId) return this.rbacDepartmentId;
      const fromScope = String(this.dashboardScope?.departmentId || "").trim();
      if (fromScope) return fromScope;
      try {
        const raw = sessionStorage.getItem("userInfo");
        const user = raw ? JSON.parse(raw) : {};
        return String(user?.departmentID || user?.departmentId || "").trim();
      } catch (_) {
        return "";
      }
    },
    showStaffDistribution() {
      if (this.dashboardScope?.departmentScoped === true) return false;
      return !this.isDepartmentalScope;
    },
    ethnicityTitle() {
      let dept = this.dashboardScope?.departmentName || '';
      if (!dept) {
        try {
          const raw = sessionStorage.getItem("userInfo");
          const user = raw ? JSON.parse(raw) : {};
          dept = String(user?.departmentName || "").trim();
        } catch (_) {}
      }
      if (this.isDepartmentalScope && dept) {
        return `${dept} Ethnicity Distribution`;
      }
      return 'Ethnicity Distribution';
    },
    showWageBillPerDepartment() {
      if (this.dashboardScope?.hideWageBillByDepartment) return false;
      if (this.isDepartmentalHrRole) return false;
      return true;
    },
    staffByDepartmentRows() {
      const rows = this.staffByDepartmentData?.rows;
      return Array.isArray(rows) ? rows : [];
    },
    currentPeriodLabel() {
      const d = new Date(this.selectedPeriodYear, this.selectedPeriodMonth - 1, 1);
      return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    },
    canGoNextPeriod() {
      const t = new Date();
      const cy = t.getFullYear();
      const cm = t.getMonth() + 1;
      return this.periodYmKey(this.selectedPeriodYear, this.selectedPeriodMonth) < this.periodYmKey(cy, cm);
    },
    attendancePeriodDisplay() {
      const label = this.attendanceData?.period?.label;
      return label || this.currentPeriodLabel;
    },
    attendanceRateDisplay() {
      const s = this.attendanceSummarySummary;
      if (!s) return null;
      return s.attendanceRatePct != null ? s.attendanceRatePct : null;
    },
    attendanceRateHint() {
      const s = this.attendanceSummarySummary;
      if (!s) return "";
      return `${this.formatNumber(s.clockedInEmployees)} of ${this.formatNumber(s.totalEmployees)} employees clocked in`;
    },
    todayIsoDate() {
      return new Date().toISOString().slice(0, 10);
    },
    attendanceChartRangeLabel() {
      if (!this.attendanceChartStartDate || !this.attendanceChartEndDate) return '';
      return `${this.attendanceChartStartDate} to ${this.attendanceChartEndDate}`;
    }
  },
  mounted() {
    this.initializeDashboard();
    this.setupAutoRefresh();
  },
  beforeDestroy() {
    this.cleanupCharts();
    if (this.autoRefreshInterval) clearInterval(this.autoRefreshInterval);
  },
  methods: {
    periodYmKey(y, m) {
      return Number(y) * 100 + Number(m);
    },
    handleNavToggle(isCollapsed) { this.isNavCollapsed = isCollapsed; },
    dashRoleRowLabel(r) {
      if (!r || typeof r !== "object") return "";
      return String(
        r.role_name ?? r.roleName ?? r.Role_Name ?? r.ROLE_NAME ?? r.name ?? ""
      ).trim();
    },
    dashStringLooksLikeGovernor(s) {
      const t = String(s || "").trim();
      if (!t) return false;
      const u = t.toUpperCase();
      const norm = u.replace(/[^A-Z0-9]/g, "");
      if (norm === "GOVERNOR" || u === "GOVERNOR") return true;
      return /\bGOVERNOR\b/i.test(t);
    },
    dashSessionIsGovernor() {
      try {
        const ctxRaw = sessionStorage.getItem("rbac_context");
        if (ctxRaw) {
          const ctx = JSON.parse(ctxRaw);
          if (this.dashStringLooksLikeGovernor(ctx?.user_category)) return true;
          const names = ctx?.role_names;
          if (Array.isArray(names) && names.some((n) => this.dashStringLooksLikeGovernor(n))) return true;
        }
      } catch (_) {}
      try {
        const rbacStr = sessionStorage.getItem("rbac_permissions");
        if (rbacStr) {
          const perms = JSON.parse(rbacStr);
          if (Array.isArray(perms) && perms.some((p) => this.dashStringLooksLikeGovernor(p))) return true;
        }
      } catch (_) {}
      try {
        const rolesRaw = sessionStorage.getItem("selectedRoles");
        const roles = rolesRaw ? JSON.parse(rolesRaw) : [];
        if (Array.isArray(roles) && roles.some((r) => this.dashStringLooksLikeGovernor(this.dashRoleRowLabel(r)))) return true;
      } catch (_) {}
      try {
        const raw = sessionStorage.getItem("userInfo");
        const u = raw ? JSON.parse(raw) : {};
        if (this.dashStringLooksLikeGovernor(u.category || u.Category)) return true;
        const des = String(u.designation || u.positionTitle || u.position_title || "").trim().toLowerCase();
        if (des === "governor" || des === "executive") return true;
      } catch (_) {}
      return false;
    },
    dashStringLooksLikeHeadOfHr(s) {
      const u = String(s || "").trim().toUpperCase();
      return u === "HEAD OF HR" || u.includes("HEAD OF HR");
    },
    dashSessionIsHeadOfHr() {
      try {
        const ctxRaw = sessionStorage.getItem("rbac_context");
        if (ctxRaw) {
          const ctx = JSON.parse(ctxRaw);
          const names = ctx?.role_names;
          if (Array.isArray(names) && names.some((n) => this.dashStringLooksLikeHeadOfHr(n))) return true;
        }
      } catch (_) {}
      try {
        const rbacStr = sessionStorage.getItem("rbac_permissions");
        if (rbacStr) {
          const perms = JSON.parse(rbacStr);
          if (Array.isArray(perms) && perms.some((p) => this.dashStringLooksLikeHeadOfHr(p))) return true;
        }
      } catch (_) {}
      try {
        const rolesRaw = sessionStorage.getItem("selectedRoles");
        const roles = rolesRaw ? JSON.parse(rolesRaw) : [];
        if (Array.isArray(roles) && roles.some((r) => this.dashStringLooksLikeHeadOfHr(this.dashRoleRowLabel(r)))) return true;
      } catch (_) {}
      return false;
    },
    initializeDashboard() {
      this.loadDashboardData();
    },
    
    async loadDashboardData() {
      try {
        this.refreshing = true;
        const data = new FormData();
        data.append("function", "getDashboard");
        data.append("year", String(this.selectedPeriodYear));
        data.append("month", String(this.selectedPeriodMonth));
        this.syncAttendanceChartRangeToPeriod(false);
        if (this.attendanceChartStartDate) data.append("chartStartDate", this.attendanceChartStartDate);
        if (this.attendanceChartEndDate) data.append("chartEndDate", this.attendanceChartEndDate);
        const res = await execute(data, authUrl);
        if (!res.data.success) {
          console.error('Dashboard API error:', res.data.message);
          return;
        }
        const d = res.data.data || {};
        this.dashboardScope = d.scope || this.dashboardScope;
        if (this.dashSessionIsGovernor() && !this.dashSessionIsHeadOfHr()) {
          this.dashboardScope = {
            ...this.dashboardScope,
            type: "governor",
            departmentId: "",
            departmentName: "",
            departmentScoped: false,
            hideWageBillByDepartment: false
          };
        }
        this.metrics = d.metrics || this.metrics;
        const attSummary = d.attendanceSummary || {};
        if (attSummary.period && attSummary.period.year != null && attSummary.period.month != null) {
          this.selectedPeriodYear = attSummary.period.year;
          this.selectedPeriodMonth = attSummary.period.month;
        }
        this.attendanceSummarySummary = attSummary.summary || null;
        const chartRange = attSummary.chartData?.range || {};
        if (chartRange.startDate) this.attendanceChartStartDate = chartRange.startDate;
        if (chartRange.endDate) this.attendanceChartEndDate = chartRange.endDate;
        this.attendanceData = {
          chartData: attSummary.chartData,
          year: attSummary.year,
          month: attSummary.month,
          summary: attSummary.summary,
          period: attSummary.period
        };
        const todayAtt = d.todayAttendance || {};
        this.todayAttendanceData = { chartData: todayAtt.chartData };
        this.todayAttendanceStats = {
          present: todayAtt.summary?.present ?? 0,
          absent: todayAtt.summary?.absent ?? 0,
          late: 0,
          total: (todayAtt.summary?.present ?? 0) + (todayAtt.summary?.absent ?? 0) + (todayAtt.summary?.onLeave ?? 0)
        };
        this.staffByDepartmentData = d.staffByDepartment || null;
        this.staffTurnoverData = d.staffTurnover || null;
        this.natureOfExitData = d.natureOfExits || null;
        this.genderComparisonData = d.genderComparison || null;
        this.disabilityDistribution = d.disabilityDistribution || null;
        this.employeeCompositionData = d.employeeComposition || null;
        this.jobGroupsData = d.jobGroups || null;
        this.wageBillByDepartmentData = d.wageBillByDepartment || null;
        this.loadEthnicityDistribution(this.effectiveDepartmentId);
        this.getActiveEmployeesTrend();
        this.calculateExitSummary();
        this.calculateGenderStats();
        this.calculateDisabilityStats();
        this.$nextTick(() => {
          this.renderAttendanceChart();
          this.renderTodayAttendanceChart();
          if (this.showStaffDistribution) this.renderStaffByDepartmentChart();
          this.renderNatureOfExitsChart();
          this.renderEmployeeCompositionChart();
          this.renderJobGroupsChart();
          if (this.showWageBillPerDepartment) this.renderWageBillChart();
          this.renderGenderComparisonChart();
          this.renderDisabilityDistributionChart();
          this.renderActiveEmployeesTrendChart();
        });
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        this.refreshing = false;
      }
    },
    
    refreshDashboard() {
      this.loadDashboardData();
    },
    
    setupAutoRefresh() {
      this.autoRefreshInterval = setInterval(() => {
        if (document.visibilityState === 'visible') this.loadDashboardData();
      }, 300000);
    },
    
    goToEmployeeRegister() { this.$router.push('/employee-register'); },
    goToHRActions() { this.$router.push('/hr-actions'); },
    goToAttendance() { this.$router.push('/report-monthly'); },
    goToLeaveManagement() { this.$router.push('/attendance-leave'); },
    logoutDashboard() {
      sessionStorage.clear();
      this.$router.push('/');
    },
    async loadEthnicityDistribution(departmentId = '') {
      try {
        const data = new FormData();
        data.append("function", "getEmployees");
        data.append("page", "1");
        data.append("rows_per_page", "5000");
        data.append("status", "Active");
        // PHP getEmployees filters on `department`, not `departmentId` (departmentId is now also accepted server-side)
        if (departmentId) {
          data.append("department", String(departmentId));
          data.append("departmentId", String(departmentId));
        }
        const res = await execute(data, authUrl);
        let list = res?.data?.employees || res?.data?.data?.employees || [];
        const scopeName = String(this.dashboardScope?.departmentName || "").trim().toLowerCase();
        if (this.dashboardScope?.departmentScoped === true && scopeName) {
          list = (Array.isArray(list) ? list : []).filter((emp) => {
            const dn = String(emp?.departmentName || "").trim().toLowerCase();
            return dn === scopeName;
          });
        }
        const counts = {};
        (Array.isArray(list) ? list : []).forEach((emp) => {
          const key = String(
            emp?.ethnicity ||
            emp?.ethnicGroup ||
            emp?.tribe ||
            emp?.community ||
            "Not specified"
          ).trim() || "Not specified";
          counts[key] = (counts[key] || 0) + 1;
        });
        this.ethnicityDistribution = Object.keys(counts)
          .map((k) => ({ name: k, count: counts[k] }))
          .sort((a, b) => b.count - a.count);
      } catch (_) {
        this.ethnicityDistribution = [];
      }
    },
    
    periodMonthBounds(year, month) {
      const m = String(month).padStart(2, '0');
      const start = `${year}-${m}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const endFull = `${year}-${m}-${String(lastDay).padStart(2, '0')}`;
      const today = this.todayIsoDate;
      const end = endFull > today ? today : endFull;
      return { start, end };
    },
    syncAttendanceChartRangeToPeriod(force = true) {
      const { start, end } = this.periodMonthBounds(this.selectedPeriodYear, this.selectedPeriodMonth);
      if (force || !this.attendanceChartStartDate) this.attendanceChartStartDate = start;
      if (force || !this.attendanceChartEndDate) this.attendanceChartEndDate = end;
    },
    onAttendanceChartRangeChange() {
      if (!this.attendanceChartStartDate || !this.attendanceChartEndDate) return;
      if (this.attendanceChartStartDate > this.attendanceChartEndDate) {
        const tmp = this.attendanceChartStartDate;
        this.attendanceChartStartDate = this.attendanceChartEndDate;
        this.attendanceChartEndDate = tmp;
      }
      this.loadAttendanceChart();
    },
    previousPeriod() {
      let y = this.selectedPeriodYear;
      let m = this.selectedPeriodMonth - 1;
      if (m < 1) {
        m = 12;
        y -= 1;
      }
      if (y < 2000) return;
      this.selectedPeriodYear = y;
      this.selectedPeriodMonth = m;
      this.syncAttendanceChartRangeToPeriod(true);
      this.loadDashboardData();
    },
    nextPeriod() {
      if (!this.canGoNextPeriod) return;
      let y = this.selectedPeriodYear;
      let m = this.selectedPeriodMonth + 1;
      if (m > 12) {
        m = 1;
        y += 1;
      }
      this.selectedPeriodYear = y;
      this.selectedPeriodMonth = m;
      this.syncAttendanceChartRangeToPeriod(true);
      this.loadDashboardData();
    },
    
    formatNumber(value) { return value ? parseInt(value).toLocaleString() : '0'; },
    staffDeptShare(count) {
      const t = Number(this.staffByDepartmentData?.total || 0);
      const c = Number(count || 0);
      if (!t || !c) return '—';
      return `${((c / t) * 100).toFixed(1)}%`;
    },
    formatWageBillKes(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return '0';
      return n.toLocaleString('en-KE', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    },
    
    getEmployeeMetrics() {
      const data = new FormData();
      data.append("function", "getEmployeeMetrics");
      return execute(data, authUrl).then((res) => {
        if (res.data.success) this.metrics = res.data.data.metrics;
      }).catch(console.error);
    },
    
    getAttendanceSummary() {
      return this.loadAttendanceChart();
    },
    loadAttendanceChart() {
      const data = new FormData();
      data.append("function", "getAttendanceSummary");
      data.append("year", String(this.selectedPeriodYear));
      data.append("month", String(this.selectedPeriodMonth));
      if (this.attendanceChartStartDate) data.append("chartStartDate", this.attendanceChartStartDate);
      if (this.attendanceChartEndDate) data.append("chartEndDate", this.attendanceChartEndDate);
      this.attendanceChartLoading = true;
      return execute(data, authUrl).then((res) => {
        if (res.data.success) {
          const payload = res.data.data || {};
          const chartRange = payload.chartData?.range || payload.range || {};
          if (chartRange.startDate) this.attendanceChartStartDate = chartRange.startDate;
          if (chartRange.endDate) this.attendanceChartEndDate = chartRange.endDate;
          if (payload.summary) {
            this.attendanceSummarySummary = payload.summary;
          }
          this.attendanceData = {
            ...this.attendanceData,
            chartData: payload.chartData,
            year: payload.year,
            month: payload.month,
            summary: payload.summary || this.attendanceSummarySummary
          };
          this.$nextTick(() => this.renderAttendanceChart());
        }
      }).catch(console.error).finally(() => {
        this.attendanceChartLoading = false;
      });
    },
    
    getTodayAttendance() {
      const data = new FormData();
      data.append("function", "getTodayAttendance");
      return execute(data, authUrl).then((res) => {
        if (res.data.success) {
          this.todayAttendanceData = res.data.data;
          this.calculateTodayAttendanceStats();
          this.$nextTick(() => this.renderTodayAttendanceChart());
        }
      }).catch(console.error);
    },
    
    refreshTodayAttendance() { this.getTodayAttendance(); },
    
    calculateTodayAttendanceStats() {
      if (!this.todayAttendanceData?.chartData?.series?.[0]?.data) return;
      const data = this.todayAttendanceData.chartData.series[0].data;
      this.todayAttendanceStats = {
        present: data.find(d => d.name === 'Present')?.y || 0,
        absent: data.find(d => d.name === 'Absent')?.y || 0,
        late: data.find(d => d.name === 'Late')?.y || 0,
        total: data.reduce((sum, item) => sum + (item.y || 0), 0)
      };
    },
    
    getStaffTurnover() {
      const data = new FormData();
      data.append("function", "getStaffTurnover");
      return execute(data, authUrl).then((res) => {
        if (res.data.success) {
          this.staffTurnoverData = res.data.data;
        }
      }).catch(console.error);
    },
    
    getNatureOfExits() {
      const data = new FormData();
      data.append("function", "getNatureOfExits");
      return execute(data, authUrl).then((res) => {
        if (res.data.success) {
          this.natureOfExitData = res.data.data;
          this.calculateExitSummary();
          this.$nextTick(() => this.renderNatureOfExitsChart());
        }
      }).catch(console.error);
    },
    
    calculateExitSummary() {
      if (!this.natureOfExitData?.chartData?.series?.[0]?.data) return;
      this.exitSummary = this.natureOfExitData.chartData.series[0].data
        .map(item => ({ name: item.name, value: item.y }))
        .slice(0, 5);
    },
    
    getGenderComparison() {
      const data = new FormData();
      data.append("function", "getGenderComparison");
      return execute(data, authUrl).then((res) => {
        if (res.data.success) {
          this.genderComparisonData = res.data.data;
          this.calculateGenderStats();
          this.$nextTick(() => this.renderGenderComparisonChart());
        }
      }).catch(console.error);
    },
    
    calculateGenderStats() {
      if (!this.genderComparisonData?.chartData?.series?.[0]?.data) return;
      this.genderComparisonData.chartData.series[0].data.forEach(item => {
        const name = item.name.toLowerCase();
        if (name.includes('male') && !name.includes('female')) this.genderStats.male = item.y;
        else if (name.includes('female')) this.genderStats.female = item.y;
        else this.genderStats.other = (this.genderStats.other || 0) + item.y;
      });
    },
    
    getDisabilityDistribution() {
      const data = new FormData();
      data.append("function", "getDisabilityDistribution");
      return execute(data, authUrl).then((res) => {
        if (res.data.success) {
          this.disabilityDistribution = res.data.data;
          this.calculateDisabilityStats();
          this.$nextTick(() => this.renderDisabilityDistributionChart());
        }
      }).catch(console.error);
    },
    
    calculateDisabilityStats() {
      if (!this.disabilityDistribution?.chartData?.series?.[0]?.data) return;
      const data = this.disabilityDistribution.chartData.series[0].data;
      const withDisabilities = data.find(d => d.name === 'With Disabilities')?.y || 0;
      const withoutDisabilities = data.find(d => d.name === 'Without Disabilities')?.y || 0;
      const total = withDisabilities + withoutDisabilities;
      this.disabilityStats = {
        withDisabilities: withDisabilities,
        total: total,
        percentage: total > 0 ? ((withDisabilities / total) * 100).toFixed(1) : 0
      };
    },
    
    renderAttendanceChart() {
      if (!this.$refs.attendanceChart || !this.attendanceData) {
        return;
      }
      if (this.chartInstances.attendance) this.chartInstances.attendance.destroy();

      const chartData = this.attendanceData.chartData;
      if (!chartData || !chartData.series || !Array.isArray(chartData.series)) {
        return;
      }

      const isDaily = chartData.chartType === 'daily_clockins';
      const categories = chartData.xAxis?.categories || [];
      const dateMeta = chartData.meta?.dates || [];
      const firstPoint = chartData.series[0]?.data?.[0];
      const isLegacyMonthly = !isDaily && firstPoint != null && typeof firstPoint === 'object' && firstPoint.y !== undefined;

      let series;
      let xCategories = categories;
      let chartType = 'column';

      if (isDaily) {
        const multiSeries = (chartData.series || []).length > 1;
        chartType = multiSeries ? 'column' : 'line';
        series = chartData.series.map((s) => ({
          type: multiSeries ? 'column' : 'line',
          name: s.name || 'Employees clocked in',
          data: s.data || [],
          color: s.color || '#0d9488',
          marker: multiSeries ? undefined : { enabled: categories.length <= 31, radius: 3 },
          lineWidth: multiSeries ? undefined : 2
        }));
      } else if (isLegacyMonthly) {
        const d0 = chartData.series[0]?.data || [];
        const legCat = d0.map((p) => (p && p.name != null ? String(p.name) : ''));
        if (legCat.some(Boolean)) xCategories = legCat;
        series = chartData.series.map((s) => ({
          type: 'column',
          name: s.name,
          data: (s.data || []).map((p) => (typeof p === 'object' && p != null && p.y !== undefined ? p.y : p))
        }));
      } else {
        series = chartData.series.map((s) => ({
          type: 'column',
          name: s.name,
          data: s.data || [],
          color: s.color
        }));
      }

      const tickInterval = isDaily && xCategories.length > 20 ? Math.ceil(xCategories.length / 12) : 1;

      this.chartInstances.attendance = Highcharts.chart(this.$refs.attendanceChart, {
        chart: { type: chartType, height: isDaily ? 320 : 300, backgroundColor: 'transparent' },
        title: { text: null },
        xAxis: {
          categories: xCategories,
          crosshair: true,
          tickInterval,
          labels: {
            style: { fontSize: '11px', fontWeight: '500' },
            rotation: isDaily && xCategories.length > 14 ? -45 : 0
          },
          gridLineWidth: 0
        },
        yAxis: {
          min: 0,
          allowDecimals: false,
          title: {
            text: isDaily ? 'Employees' : (isLegacyMonthly ? 'Percentage (%)' : 'Count'),
            style: { fontWeight: '600' }
          },
          labels: isLegacyMonthly ? { format: '{value}%' } : { format: '{value:,.0f}' },
          gridLineColor: '#e5e7eb'
        },
        legend: { enabled: isDaily && (chartData.series || []).length > 1, align: 'center', verticalAlign: 'bottom' },
        tooltip: {
          shared: true,
          backgroundColor: 'rgba(255, 255, 255, 0.96)',
          borderColor: '#e5e7eb',
          borderRadius: 8,
          formatter() {
            const idx = this.points?.[0]?.point?.index;
            const isoDate = idx != null && dateMeta[idx] ? dateMeta[idx] : this.x;
            let s = `<b>${isoDate}</b>`;
            (this.points || []).forEach((pt) => {
              const y = isLegacyMonthly
                ? `${Highcharts.numberFormat(pt.y, 1)}%`
                : Highcharts.numberFormat(pt.y, 0, '.', ',');
              s += `<br/><span style="color:${pt.color}">\u25CF</span> ${pt.series.name}: <b>${y}</b>`;
            });
            return s;
          }
        },
        plotOptions: {
          line: {
            marker: { symbol: 'circle' }
          },
          column: {
            pointPadding: 0.08,
            groupPadding: 0.12,
            borderWidth: 0,
            borderRadius: 4,
            dataLabels: { enabled: false }
          }
        },
        series
      });
    },
    
    renderTodayAttendanceChart() {
      if (!this.$refs.todayAttendanceChart || !this.todayAttendanceData) return;
      if (this.chartInstances.todayAttendance) this.chartInstances.todayAttendance.destroy();
      
      this.chartInstances.todayAttendance = Highcharts.chart(this.$refs.todayAttendanceChart, {
        chart: { type: 'pie', height: 230 },
        title: { text: null },
        plotOptions: { pie: { allowPointSelect: true, dataLabels: { enabled: true, format: '{point.name}: {point.y}' }, showInLegend: true } },
        series: this.todayAttendanceData.chartData?.series || [{ name: 'Attendance', data: [] }]
      });
    },
    
    renderStaffByDepartmentChart() {
      if (!this.$refs.staffDeptChart || !this.staffByDepartmentData) {
        return;
      }
      if (this.chartInstances.staffDept) this.chartInstances.staffDept.destroy();

      const chartData = this.staffByDepartmentData.chartData;
      if (!chartData || !chartData.series || !Array.isArray(chartData.series)) {
        return;
      }

      const categories = chartData.xAxis?.categories || [];
      const n = categories.length;
      const chartHeight = Math.min(520, Math.max(260, 48 + n * 22));

      this.chartInstances.staffDept = Highcharts.chart(this.$refs.staffDeptChart, {
        chart: { type: 'bar', height: chartHeight, backgroundColor: 'transparent', marginLeft: 120 },
        title: { text: null },
        xAxis: {
          categories,
          title: { text: null },
          labels: {
            style: { fontSize: '11px', fontWeight: '500', textOverflow: 'ellipsis' },
            formatter() {
              const v = String(this.value || '');
              return v.length > 42 ? `${v.slice(0, 40)}…` : v;
            }
          },
          gridLineWidth: 0
        },
        yAxis: {
          min: 0,
          title: { text: 'Staff count', style: { fontWeight: '600' } },
          allowDecimals: false,
          gridLineColor: '#e5e7eb'
        },
        plotOptions: {
          bar: {
            dataLabels: {
              enabled: n <= 24,
              format: '{y:,.0f}',
              style: { fontWeight: '600', textOutline: 'none', fontSize: '10px' }
            },
            borderRadius: 4,
            pointPadding: 0.12,
            groupPadding: 0.08
          }
        },
        tooltip: {
          pointFormat: '<b>{point.y:,.0f}</b> active employees',
          backgroundColor: 'rgba(255, 255, 255, 0.96)',
          borderColor: '#e5e7eb',
          borderRadius: 8
        },
        legend: { enabled: false },
        series: chartData.series
      });
    },
    
    renderNatureOfExitsChart() {
      if (!this.$refs.exitChart || !this.natureOfExitData) return;
      if (this.chartInstances.exit) this.chartInstances.exit.destroy();
      
      this.chartInstances.exit = Highcharts.chart(this.$refs.exitChart, {
        chart: { type: 'pie', height: 230 },
        title: { text: null },
        plotOptions: { pie: { allowPointSelect: true, dataLabels: { enabled: true, format: '{point.name}: {point.y}' }, showInLegend: true } },
        series: this.natureOfExitData.chartData?.series || [{ name: 'Exits', data: [] }]
      });
    },
    
    renderEmployeeCompositionChart() {
      if (!this.$refs.compositionChart || !this.employeeCompositionData) return;
      if (this.chartInstances.composition) this.chartInstances.composition.destroy();
      this.chartInstances.composition = Highcharts.chart(this.$refs.compositionChart, {
        chart: { type: 'pie', height: 230 },
        title: { text: null },
        plotOptions: { pie: { allowPointSelect: true, dataLabels: { enabled: true, format: '{point.name}: {point.y}' }, showInLegend: true } },
        series: this.employeeCompositionData.chartData?.series || [{ name: 'Employees', colorByPoint: true, data: [] }]
      });
    },
    renderJobGroupsChart() {
      if (!this.$refs.jobGroupsChart || !this.jobGroupsData) return;
      if (this.chartInstances.jobGroups) this.chartInstances.jobGroups.destroy();
      this.chartInstances.jobGroups = Highcharts.chart(this.$refs.jobGroupsChart, {
        chart: { type: 'bar', height: 280 },
        title: { text: null },
        xAxis: { categories: this.jobGroupsData.chartData?.xAxis?.categories || [], title: { text: null } },
        yAxis: { min: 0, title: { text: 'Employees' } },
        plotOptions: { bar: { dataLabels: { enabled: true } } },
        legend: { enabled: false },
        series: this.jobGroupsData.chartData?.series || [{ name: 'Employees', data: [] }]
      });
    },
    async getActiveEmployeesTrend() {
      try {
        const data = new FormData();
        data.append("function", "getActiveEmployeesByMonth");
        data.append("year", String(this.selectedPeriodYear));
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          this.activeEmployeesTrendData = res.data.data.trend || [];
          this.renderActiveEmployeesTrendChart();
        }
      } catch (err) {
        console.error("Error fetching active employees trend:", err);
      }
    },
    renderActiveEmployeesTrendChart() {
      if (!this.$refs.activeEmployeesTrendChart || !this.activeEmployeesTrendData) return;
      if (this.chartInstances.activeEmployeesTrend) this.chartInstances.activeEmployeesTrend.destroy();
      
      const categories = this.activeEmployeesTrendData.map(d => d.month);
      const data = this.activeEmployeesTrendData.map(d => d.activeCount);
      
      this.chartInstances.activeEmployeesTrend = Highcharts.chart(this.$refs.activeEmployeesTrendChart, {
        chart: { type: 'column', height: 280 },
        title: { text: null },
        xAxis: { categories: categories },
        yAxis: { min: 0, title: { text: 'Active Employees' } },
        plotOptions: { column: { dataLabels: { enabled: true, format: '{y}' }, borderRadius: 4 } },
        legend: { enabled: false },
        series: [{ name: 'Active Employees', data: data, color: '#1e4976' }]
      });
    },
    renderWageBillChart() {
      if (!this.$refs.wageBillChart || !this.wageBillByDepartmentData) return;
      if (this.chartInstances.wageBill) this.chartInstances.wageBill.destroy();
      const series = this.wageBillByDepartmentData.chartData?.series || [{ name: 'Wage bill (KES)', data: [] }];
      this.chartInstances.wageBill = Highcharts.chart(this.$refs.wageBillChart, {
        chart: { type: 'bar', height: 280 },
        title: { text: null },
        xAxis: { categories: this.wageBillByDepartmentData.chartData?.xAxis?.categories || [], title: { text: null } },
        yAxis: { min: 0, title: { text: 'KES' }, labels: { format: '{value:,.0f}' } },
        plotOptions: { bar: { dataLabels: { enabled: true, format: '{y:,.0f}' } } },
        tooltip: { pointFormat: '<b>{point.y:,.2f}</b> KES' },
        legend: { enabled: false },
        series: series
      });
    },
    renderGenderComparisonChart() {
      if (!this.$refs.genderChart || !this.genderComparisonData) return;
      if (this.chartInstances.gender) this.chartInstances.gender.destroy();

      this.chartInstances.gender = Highcharts.chart(this.$refs.genderChart, {
        chart: { type: 'pie', height: 230 },
        title: { text: null },
        plotOptions: { pie: { allowPointSelect: true, dataLabels: { enabled: true, format: '{point.name}: {point.y}' }, showInLegend: true } },
        series: this.genderComparisonData.chartData?.series || [{ name: 'Gender', data: [] }]
      });
    },
    
    renderDisabilityDistributionChart() {
      if (!this.$refs.disabilityChart || !this.disabilityDistribution) return;
      if (this.chartInstances.disability) this.chartInstances.disability.destroy();
      
      this.chartInstances.disability = Highcharts.chart(this.$refs.disabilityChart, {
        chart: { type: 'pie', height: 230 },
        title: { text: null },
        plotOptions: { pie: { allowPointSelect: true, dataLabels: { enabled: true, format: '{point.percentage:.1f}%' } } },
        series: this.disabilityDistribution.chartData?.series || [{ name: 'Disability', data: [] }]
      });
    },
    
    exportChart(chartType) {
      const key = chartType === 'turnover' ? 'staffDept' : chartType;
      const chart = this.chartInstances[key];
      if (chart) chart.exportChart({ type: 'image/png', filename: `${chartType}-chart` });
    },
    
    cleanupCharts() {
      Object.values(this.chartInstances).forEach(chart => chart?.destroy?.());
    },
    
    showHelp() { this.showHelpModal = true; },
    closeHelpModal() { this.showHelpModal = false; }
  }
}
</script>

<style scoped>
/* ===== Institutional dashboard (county / government-grade) ===== */
.analytics-dashboard {
  --gov-ink: #0a1628;
  --gov-navy: #0c2d48;
  --gov-navy-mid: #1e4976;
  --gov-navy-soft: #e8eef4;
  --gov-line: #c9d4e0;
  --gov-line-subtle: #dfe7ee;
  --gov-surface: #ffffff;
  --gov-surface-2: #f6f8fb;
  --gov-muted: #5c6b7a;
  --gov-gold: #9a6b1c;
  --gov-gold-line: #d4a84b;
  --gov-teal: #0f5c55;
  --gov-teal-soft: #e6f4f2;
  --gov-warn: #9a3412;
  --gov-warn-soft: #fff4ed;
  --gov-danger: #9f1239;
  --gov-radius: 14px;
  --gov-radius-sm: 10px;
  --gov-shadow: 0 1px 0 rgba(10, 22, 40, 0.06), 0 12px 40px rgba(10, 22, 40, 0.07);
  --gov-shadow-hover: 0 16px 48px rgba(10, 22, 40, 0.1);

  min-height: 100vh;
  color: var(--gov-ink);
  font-family: system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  font-size: 15px;
  line-height: 1.5;
  background-color: var(--gov-surface-2);
  background-image:
    radial-gradient(ellipse 120% 80% at 50% -20%, rgba(30, 73, 118, 0.09), transparent 55%),
    linear-gradient(180deg, #eef2f6 0%, #f6f8fb 32%, #f0f3f7 100%);
}

.analytics-dashboard.nav-collapsed .dashboard-content {
  margin-left: 70px;
}

.dashboard-content {
  margin-left: 280px;
  padding: 28px 28px 48px;
  transition: margin-left 0.28s cubic-bezier(0.4, 0, 0.2, 1);
}

.page-container {
  max-width: 1580px;
  margin: 0 auto;
}

/* —— Page header —— */
.page-header {
  position: relative;
  background: var(--gov-surface);
  border-radius: var(--gov-radius);
  border: 1px solid var(--gov-line-subtle);
  box-shadow: var(--gov-shadow);
  padding: 0;
  margin-bottom: 28px;
  overflow: hidden;
}

.page-header::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 5px;
  background: linear-gradient(180deg, var(--gov-navy-mid) 0%, var(--gov-navy) 100%);
}

.page-header-hero {
  border-left: none;
}

.page-header__top {
  padding: 22px 26px 18px 32px;
}

.page-eyebrow {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--gov-navy-mid);
  background: linear-gradient(90deg, var(--gov-navy-soft), transparent);
  padding: 6px 12px 6px 0;
  margin: 0 0 12px 0;
  border-radius: 4px;
}

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 20px;
}

.title-block {
  flex: 1;
  min-width: 200px;
}

.page-title {
  margin: 0 0 8px 0;
  font-size: clamp(1.45rem, 2.2vw, 1.85rem);
  font-weight: 700;
  letter-spacing: -0.025em;
  color: var(--gov-navy);
  line-height: 1.2;
}

.page-title::after {
  content: '';
  display: block;
  width: 48px;
  height: 3px;
  margin-top: 12px;
  border-radius: 2px;
  background: linear-gradient(90deg, var(--gov-gold-line), rgba(212, 168, 75, 0.25));
}

.page-subtitle {
  margin: 0;
  font-size: 0.95rem;
  color: var(--gov-muted);
  max-width: 42rem;
  font-weight: 400;
}

.header-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.period-selector {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 4px;
  border: 1px solid var(--gov-line);
  border-radius: var(--gov-radius-sm);
  background: var(--gov-surface-2);
}

.period-btn {
  background: transparent;
  border: none;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--gov-navy-mid);
  border-radius: 8px;
  transition: background 0.15s ease, color 0.15s ease;
}

.period-btn:hover {
  background: var(--gov-surface);
  color: var(--gov-navy);
}

.period-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
  pointer-events: none;
}

.current-period {
  font-size: 12px;
  font-weight: 600;
  color: var(--gov-muted);
  min-width: 88px;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.btn-gov {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 600;
  border-radius: var(--gov-radius-sm);
  border: 1px solid transparent;
  cursor: pointer;
  transition: background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, transform 0.12s ease;
}

.btn-gov:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.btn-gov--primary {
  background: linear-gradient(180deg, var(--gov-navy-mid) 0%, var(--gov-navy) 100%);
  color: #fff;
  border-color: #0a1f30;
  box-shadow: 0 2px 0 rgba(255, 255, 255, 0.12) inset, 0 4px 14px rgba(12, 45, 72, 0.25);
}

.btn-gov--primary:hover:not(:disabled) {
  filter: brightness(1.06);
  box-shadow: 0 2px 0 rgba(255, 255, 255, 0.15) inset, 0 6px 20px rgba(12, 45, 72, 0.28);
}

.btn-gov--ghost {
  background: var(--gov-surface);
  color: var(--gov-navy-mid);
  border-color: var(--gov-line);
}

.btn-gov--ghost:hover:not(:disabled) {
  background: var(--gov-surface-2);
  border-color: var(--gov-navy-mid);
  color: var(--gov-navy);
}

.spin {
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.welcome-banner {
  position: relative;
  display: flex;
  gap: 16px;
  align-items: stretch;
  margin: 0 22px 22px 28px;
  padding: 16px 20px;
  background: linear-gradient(135deg, #fafbfc 0%, var(--gov-surface) 100%);
  border: 1px solid var(--gov-line-subtle);
  border-radius: var(--gov-radius-sm);
}

.welcome-banner__accent {
  width: 4px;
  min-width: 4px;
  border-radius: 3px;
  background: linear-gradient(180deg, var(--gov-gold-line), #c49a3c);
}

.welcome-kicker {
  margin: 0 0 4px 0;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--gov-muted);
}

.welcome-title {
  margin: 0 0 6px 0;
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--gov-navy);
}

.welcome-date {
  margin: 0;
  font-size: 13px;
  color: var(--gov-muted);
}

/* —— Section chrome —— */
.dash-section {
  margin-bottom: 28px;
}

.dash-section--charts {
  margin-bottom: 0;
}

.section-heading {
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--gov-line-subtle);
}

.section-heading--compact {
  margin-bottom: 14px;
  padding-bottom: 10px;
}

.section-heading__title {
  margin: 0 0 6px 0;
  font-size: 13px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.11em;
  color: var(--gov-navy-mid);
}

.section-heading__desc {
  margin: 0;
  font-size: 13px;
  color: var(--gov-muted);
  max-width: 48rem;
}

/* —— KPI metric cards —— */
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(248px, 1fr));
  gap: 18px;
}

.metric-card {
  --metric-accent: var(--gov-navy-mid);
  --metric-icon-bg: linear-gradient(145deg, var(--gov-navy-mid), var(--gov-navy));
  position: relative;
  background: var(--gov-surface);
  border: 1px solid var(--gov-line-subtle);
  border-radius: var(--gov-radius);
  padding: 22px 22px 22px 20px;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  cursor: pointer;
  box-shadow: 0 1px 0 rgba(10, 22, 40, 0.04);
  transition: box-shadow 0.22s ease, border-color 0.22s ease, transform 0.18s ease;
}

.metric-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 16px;
  bottom: 16px;
  width: 4px;
  border-radius: 0 3px 3px 0;
  background: var(--metric-accent);
}

.metric-card:hover {
  border-color: var(--gov-line);
  box-shadow: var(--gov-shadow-hover);
  transform: translateY(-2px);
}

.metric-card--navy {
  --metric-accent: var(--gov-navy-mid);
  --metric-icon-bg: linear-gradient(145deg, #2a5f8f, var(--gov-navy));
}

.metric-card--teal {
  --metric-accent: var(--gov-teal);
  --metric-icon-bg: linear-gradient(145deg, #147a72, var(--gov-teal));
}

.metric-card--warn {
  --metric-accent: var(--gov-warn);
  --metric-icon-bg: linear-gradient(145deg, #c2410c, var(--gov-warn));
}

.metric-card--slate {
  --metric-accent: #475569;
  --metric-icon-bg: linear-gradient(145deg, #64748b, #475569);
}

.metric-icon {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  background: var(--metric-icon-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 24px;
  box-shadow: 0 6px 16px rgba(10, 22, 40, 0.18);
}

.metric-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.metric-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--gov-muted);
  margin-bottom: 8px;
}

.metric-value {
  font-size: clamp(1.65rem, 3vw, 2rem);
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: var(--gov-navy);
  line-height: 1.15;
  margin-bottom: 8px;
}

.metric-trend {
  font-size: 12px;
  font-weight: 700;
  color: var(--gov-navy-mid);
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.metric-trend.negative {
  color: var(--gov-danger);
}

.metric-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--gov-surface-2);
  border: 1px solid var(--gov-line);
  color: var(--gov-navy-mid);
  align-self: flex-start;
}

/* —— Charts —— */
.charts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: 20px;
  padding-bottom: 8px;
}

.chart-card-wide {
  grid-column: 1 / -1;
}

.chart-card {
  background: var(--gov-surface);
  border: 1px solid var(--gov-line-subtle);
  border-radius: var(--gov-radius);
  display: flex;
  flex-direction: column;
  min-height: 340px;
  box-shadow: var(--gov-shadow);
  transition: box-shadow 0.22s ease, border-color 0.22s ease;
}

.chart-card:hover {
  box-shadow: var(--gov-shadow-hover);
  border-color: var(--gov-line);
}

.card-header {
  padding: 18px 22px;
  border-bottom: 1px solid var(--gov-line-subtle);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  background: linear-gradient(180deg, #fbfcfd 0%, var(--gov-surface) 100%);
}

.card-header__text {
  flex: 1;
  min-width: 0;
}

.card-title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--gov-navy);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  letter-spacing: -0.01em;
}

.card-title i {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  font-size: 18px;
  color: var(--gov-navy-mid);
  background: var(--gov-navy-soft);
  border: 1px solid var(--gov-line-subtle);
}

.card-desc {
  margin: 8px 0 0 0;
  font-size: 12px;
  line-height: 1.55;
  color: var(--gov-muted);
  max-width: 52rem;
}

.card-desc strong {
  color: var(--gov-navy-mid);
  font-weight: 600;
}

.card-meta-line {
  margin: 8px 0 0 0;
  font-size: 12px;
  color: var(--gov-muted);
}

.card-meta-line strong {
  color: var(--gov-navy);
  font-weight: 700;
}

.card-controls {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 8px;
}

.attendance-chart-controls {
  flex-wrap: wrap;
  justify-content: flex-end;
}

.chart-date-label {
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  color: var(--gov-muted);
}

.chart-date-label span {
  line-height: 1;
}

.chart-date-input {
  height: 34px;
  padding: 0 10px;
  border: 1px solid var(--gov-line);
  border-radius: 8px;
  background: var(--gov-surface);
  color: var(--gov-navy);
  font-size: 12px;
  font-weight: 600;
  min-width: 132px;
}

.chart-date-input:focus {
  outline: none;
  border-color: var(--gov-navy-mid);
  box-shadow: 0 0 0 2px rgba(15, 76, 129, 0.12);
}

.control-btn {
  width: 36px;
  height: 36px;
  border: 1px solid var(--gov-line);
  background: var(--gov-surface);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--gov-navy-mid);
  font-size: 17px;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.control-btn:hover {
  border-color: var(--gov-navy-mid);
  color: var(--gov-navy);
  background: var(--gov-navy-soft);
}

.year-control {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 3px;
  border: 1px solid var(--gov-line);
  border-radius: 10px;
  background: var(--gov-surface-2);
}

.year-control .control-btn {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
}

.current-year {
  font-size: 13px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: var(--gov-navy);
  min-width: 52px;
  text-align: center;
}

.date-badge,
.period-badge {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--gov-navy-mid);
  background: var(--gov-navy-soft);
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid var(--gov-line-subtle);
}

.card-body {
  padding: 18px 22px 22px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.attendance-kpis {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
}

.kpi-pill {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 11px 14px;
  border-radius: var(--gov-radius-sm);
  background: var(--gov-surface-2);
  border: 1px solid var(--gov-line-subtle);
  min-width: 118px;
}

.kpi-pill.kpi-primary {
  background: linear-gradient(135deg, var(--gov-teal-soft) 0%, #eef6ff 100%);
  border-color: #b8d4e8;
}

.kpi-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 700;
  color: var(--gov-muted);
}

.kpi-value {
  font-size: 17px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: var(--gov-navy);
}

.kpi-hint {
  font-size: 11px;
  line-height: 1.35;
  color: var(--gov-muted);
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
}

.attendance-summary-footnote {
  margin: 0 0 14px;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.5;
  color: #475569;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.card-body-split {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

@media (min-width: 1024px) {
  .card-body-split {
    flex-direction: row;
    align-items: stretch;
  }
  .staff-chart-wrap {
    flex: 1.2;
    min-width: 0;
  }
  .staff-table-wrap {
    flex: 0 0 320px;
    max-width: 360px;
  }
}

.staff-chart-wrap {
  min-height: 200px;
}

.staff-table-wrap {
  border: 1px solid var(--gov-line-subtle);
  border-radius: var(--gov-radius-sm);
  overflow: hidden;
  background: var(--gov-surface-2);
}

.staff-table-head {
  padding: 11px 14px;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--gov-navy-mid);
  background: linear-gradient(180deg, #eef2f6, #e4eaf0);
  border-bottom: 1px solid var(--gov-line);
}

.staff-table-scroll {
  max-height: 320px;
  overflow: auto;
}

.staff-dept-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.staff-dept-table th,
.staff-dept-table td {
  padding: 9px 14px;
  border-bottom: 1px solid var(--gov-line-subtle);
  text-align: left;
}

.staff-dept-table th {
  position: sticky;
  top: 0;
  background: var(--gov-surface);
  z-index: 1;
  font-weight: 700;
  color: var(--gov-navy-mid);
}

.staff-dept-table .num {
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.staff-dept-table tbody tr:hover {
  background: rgba(255, 255, 255, 0.85);
}

.text-muted {
  color: var(--gov-muted) !important;
}

#AttendanceContainer,
#TodayAttendanceContainer,
#StaffDeptContainer,
#ExitChart,
#GenderContainer,
#disabilityChart,
#compositionChart,
#jobGroupsChart,
#wageBillChart {
  flex: 1;
  min-height: 220px;
}

.stats-row {
  display: flex;
  gap: 12px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--gov-line-subtle);
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--gov-line-subtle);
}

.stat-item {
  flex: 1;
  text-align: center;
  padding: 10px 8px;
  border-radius: 10px;
  background: var(--gov-surface-2);
  border: 1px solid var(--gov-line-subtle);
}

.stat-row-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  padding: 6px 0;
  border-bottom: 1px dashed var(--gov-line-subtle);
}

.stat-row-item:last-child {
  border-bottom: none;
}

.stat-label {
  display: block;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--gov-muted);
  margin-bottom: 6px;
}

.stat-value {
  display: block;
  font-size: 1.25rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: var(--gov-navy);
}

.stat-value--present {
  color: var(--gov-navy-mid);
}

.stat-value--absent {
  color: var(--gov-danger);
}

.stat-value--late {
  color: var(--gov-gold);
}

.chart-placeholder {
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--gov-muted);
  font-size: 13px;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 2px solid var(--gov-line);
  border-top-color: var(--gov-navy-mid);
  border-radius: 50%;
  animation: spin 0.65s linear infinite;
  margin-bottom: 10px;
}

.spinner.sm {
  width: 22px;
  height: 22px;
}

/* Data table (ethnicity etc.) */
.data-table-scroll {
  max-height: 280px;
  overflow: auto;
  border: 1px solid var(--gov-line-subtle);
  border-radius: var(--gov-radius-sm);
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.data-table thead {
  position: sticky;
  top: 0;
  z-index: 1;
}

.data-table th {
  text-align: left;
  padding: 10px 14px;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--gov-navy-mid);
  background: linear-gradient(180deg, #eef2f6, #e8edf2);
  border-bottom: 1px solid var(--gov-line);
}

.data-table td {
  padding: 10px 14px;
  border-bottom: 1px solid var(--gov-line-subtle);
  color: var(--gov-ink);
}

.data-table .num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.data-table tbody tr:hover td {
  background: var(--gov-surface-2);
}

.data-table__empty {
  text-align: center;
  color: var(--gov-muted);
  font-style: italic;
  padding: 24px 14px !important;
}

/* Legacy / modal */
.footer {
  background: var(--gov-surface);
  border: 1px solid var(--gov-line-subtle);
  border-radius: var(--gov-radius-sm);
  padding: 16px 20px;
  margin-top: 24px;
}

.footer-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.footer-left p,
.footer-right p {
  margin: 0;
  font-size: 13px;
  color: var(--gov-muted);
}

.system-info {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
}

.system-info i {
  color: var(--gov-navy-mid);
}

.powered-by {
  font-style: italic;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(10, 22, 40, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.modal-container {
  background: var(--gov-surface);
  border-radius: var(--gov-radius);
  width: 90%;
  max-width: 400px;
  border: 1px solid var(--gov-line);
  box-shadow: var(--gov-shadow-hover);
}

.modal-header {
  padding: 16px 20px;
  border-bottom: 1px solid var(--gov-line-subtle);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--gov-navy);
}

.modal-close {
  background: none;
  border: none;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--gov-muted);
  font-size: 20px;
  border-radius: 8px;
}

.modal-close:hover {
  color: var(--gov-danger);
  background: #fff1f2;
}

.modal-body {
  padding: 20px;
}

@media (max-width: 1024px) {
  .dashboard-content {
    margin-left: 70px;
    padding: 20px 18px 36px;
  }
}

@media (max-width: 768px) {
  .dashboard-content {
    margin-left: 0;
    padding: 16px 14px 32px;
  }

  .header-row {
    flex-direction: column;
    align-items: stretch;
  }

  .header-actions {
    justify-content: flex-start;
  }

  .metrics-grid {
    grid-template-columns: 1fr;
  }

  .charts-grid {
    grid-template-columns: 1fr;
  }

  .page-header__top {
    padding: 18px 18px 14px 24px;
  }

  .welcome-banner {
    margin-left: 18px;
    margin-right: 18px;
  }

  .footer-content {
    flex-direction: column;
    text-align: center;
  }
}
</style>